import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { 
  CreateTrainingEnrollmentDto, 
  UpdateTrainingEnrollmentDto,
  BatchEnrollmentDto 
} from '../types/dtos/training';
import { createTrainingEnrollmentService } from '../services/training-enrollment.service';

const TRAINING_ENROLLMENTS_KEY = 'training-enrollments';

// List all enrollments for an organization
export const useTrainingEnrollments = (
  organizationId: string, 
  params?: PaginationParams
) => {
  const enrollmentService = createTrainingEnrollmentService(organizationId);

  return useQuery({
    queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, params],
    queryFn: () => enrollmentService.findAll(params).then((res) => res.data),
    enabled: !!organizationId
  });
};

// List enrollments for a specific training
export const useTrainingEnrollmentsByTraining = (
  organizationId: string,
  trainingId: string,
  params?: PaginationParams
) => {
  const enrollmentService = createTrainingEnrollmentService(organizationId, trainingId);

  return useQuery({
    queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, 'training', trainingId, params],
    queryFn: () => enrollmentService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!trainingId
  });
};

// Get single enrollment
export const useTrainingEnrollment = (
  organizationId: string,
  enrollmentId: string
) => {
  const enrollmentService = createTrainingEnrollmentService(organizationId);

  return useQuery({
    queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, enrollmentId],
    queryFn: () => enrollmentService.findById(enrollmentId).then((res) => res.data),
    enabled: !!organizationId && !!enrollmentId
  });
};

// Create single enrollment
export const useCreateTrainingEnrollment = (organizationId: string) => {
  const queryClient = useQueryClient();
  const enrollmentService = createTrainingEnrollmentService(organizationId);

  return useMutation({
    mutationFn: (data: CreateTrainingEnrollmentDto) => 
      enrollmentService.create(data).then((res) => res.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId] });
      if (data.trainingId) {
        queryClient.invalidateQueries({ 
          queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, 'training', data.trainingId] 
        });
      }
    }
  });
};

// Batch enroll multiple organizations to a training
export const useBatchEnrollTraining = (
  organizationId: string,
  trainingId: string
) => {
  const queryClient = useQueryClient();
  const enrollmentService = createTrainingEnrollmentService(organizationId, trainingId);

  return useMutation({
    mutationFn: (data: BatchEnrollmentDto) => 
      enrollmentService.batchEnroll(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId] });
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, 'training', trainingId] 
      });
    }
  });
};

// Batch enroll all organizations in a group to a training
export const useBatchEnrollTrainingByGroup = (
  organizationId: string,
  trainingId: string
) => {
  const queryClient = useQueryClient();
  const enrollmentService = createTrainingEnrollmentService(organizationId, trainingId);

  return useMutation({
    mutationFn: (groupId: string) => 
      enrollmentService.batchEnrollByGroup(groupId).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId] });
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, 'training', trainingId] 
      });
    }
  });
};

// Update enrollment
export const useUpdateTrainingEnrollment = (organizationId: string) => {
  const queryClient = useQueryClient();
  const enrollmentService = createTrainingEnrollmentService(organizationId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingEnrollmentDto }) =>
      enrollmentService.update(id, data).then((res) => res.data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, id] });
      if (data.trainingId) {
        queryClient.invalidateQueries({ 
          queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId, 'training', data.trainingId] 
        });
      }
    }
  });
};

// Delete enrollment
export const useDeleteTrainingEnrollment = (organizationId: string) => {
  const queryClient = useQueryClient();
  const enrollmentService = createTrainingEnrollmentService(organizationId);

  return useMutation({
    mutationFn: (id: string) => enrollmentService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_ENROLLMENTS_KEY, organizationId] });
    }
  });
};

